<script>
  import { getContext, onMount } from 'svelte';
  import { Route, Router } from 'svelte-navigator';

  import Redirect from '@client/app/redirect.svelte';

  import { Loader } from '@client/components/loader';

  import { CampaignsService } from '@client/services/campaigns';
  import { GroupsService } from '@client/services/groups';
  import { TagsService } from '@client/services/tags';
  import { TargetingService } from '@client/services/targeting';

  import { generatePastelColors } from '@common/utils/colors';

  import { Accounts } from './accounts';
  import { Diagnostics } from './diagnostics';
  import { Groups } from './groups';

  const { basepath } = getContext('config');
  const colors = generatePastelColors(1000);
  const colorAssignments = {};

  let loading = false;

  let campaigns = [];
  let domains = [];
  let groups = [];
  let recipients = [];
  let tags = [];

  const createDatasetOptions = (value) => {
    if (!colorAssignments[value]) {
      colorAssignments[value] = colors.pop();
    }

    return {
      color: colorAssignments[value],
      label: value,
      value: value
    };
  };

  const getCampaigns = async () => {
    const response = await CampaignsService.getAll();
    campaigns = Array.isArray(response) ? response.map(({ rulename }) => createDatasetOptions(rulename)) : [];
  };

  const getGroups = async () => {
    const response = await GroupsService.getSenderGroups();
    groups = Array.isArray(response) ? response.map(({ groupname }) => createDatasetOptions(groupname)) : [];
  };

  const getTags = async (definedTags = []) => {
    const response = await TagsService.getAll();
    const catalog = Array.isArray(response) ? response : [];
    const merged = [...new Set([...catalog, ...definedTags])];
    tags = merged.map((text) => createDatasetOptions(text));
  };

  const initialize = async () => {
    loading = true;

    const targeting = await TargetingService.get();
    const accounts = Array.isArray(targeting?.accounts) ? targeting.accounts : [];
    const departments = Array.isArray(targeting?.departments) ? targeting.departments : [];
    const definedTags = [
      ...accounts.flatMap(({ tags }) => (tags || []).map(({ text }) => text)),
      ...departments.flatMap(({ tags }) => (tags || []).map(({ text }) => text))
    ].filter(Boolean);

    domains = [];
    recipients = [];

    accounts.forEach((account) => {
      account.campaigns = account.ads.map(({ text }) => createDatasetOptions(text));
      account.tags = account.tags.map(({ text }) => createDatasetOptions(text));

      if (account.name.includes('@')) {
        recipients.push(account);
      } else {
        domains.push(account);
      }
    });

    domains = [...domains].sort((a, b) => a.name.localeCompare(b.name));
    recipients = [...recipients].sort((a, b) => a.name.localeCompare(b.name));

    getCampaigns();
    getGroups();
    getTags(definedTags);

    loading = false;
  };

  onMount(async () => await initialize());

  const onRefresh = async () => await initialize();
</script>

<Loader backdrop show={loading} />

<Router basepath="{basepath}/marketing/targeting">
  <Route path="groups">
    <Groups {campaigns} {tags} {groups} {onRefresh} />
  </Route>
  <Route path="domains">
    <Accounts isDomains bind:data={domains} {campaigns} {tags} {onRefresh} />
  </Route>
  <Route path="recipients">
    <Accounts bind:data={recipients} {campaigns} {tags} {onRefresh} />
  </Route>
  <Route path="diagnostics">
    <Diagnostics />
  </Route>
  <Route path="/*">
    <Redirect path="{basepath}/marketing/targeting/groups" config={{ replace: true }} />
  </Route>
</Router>
